import React from "react";
import { motion, Variants } from "framer-motion";
import { Landmark, Building2, Factory, Home, Calendar, Hospital } from "lucide-react";

const ConstructProject: React.FC<{ content?: any }> = ({ content }) => {
  const fallbackProjects = [
    {
      title: "82 Housing Units and Services at Wilayat Muqshin",
      desc: "This project has been awarded to hamdan trading group by ministry of housing, sultanate of oman.",
      iconName: 'Home',
      category: "Residential",
      status: "Completed",
    },
    {
      title: "Physiotherapy Clinic for His Majesty",
      desc: "This prestigious project was completed in bait al mamurah palace in salalah for his majesty.",
      iconName: "Hospital",
      category: "Healthcare",
      status: "Completed",
    },
    {
      title: "General Area Office Building For Royal Court Affairs",
      desc: "This project was also awarded by royal court affairs to be constructed in salalah.",
      iconName: "Building2",
      category: "Commercial",
      status: "Completed",
    },
    {
      title: "Salalah Flour Mill",
      desc: "This project was executed by hamdan trading group and is a land mark for salalah.",
      iconName: "Factory",
      category: "Industrial",
      status: "Completed",
    },
    {
      title: "Raysut Cement Factory",
      desc: "This project was executed by hamdan trading group and is a land mark for salalah.",
      iconName: "Factory",
      category: "Industrial",
      status: "Completed",
    },
    {
      title: "Hamdan Plaza Building",
      desc: "Executed projects for the Ministry of Defence in Aydam, Ash Shuwaymiyah & Salalah.",
      iconName: "Building2",
      category: "Commercial",
      status: "Completed",
    },
  ];
  const projectsList = content?.projects?.length ? content.projects : fallbackProjects;
  const projectHeading = content?.title === "OUR PROJECTS" ? "Our Projects" : (content?.title || "Our Projects");

  const iconMap: Record<string, any> = { Landmark, Building2, Factory, Home, Calendar, Hospital };
  const filterCategories = ["Civil Works", "Housing", "Infrastructure"];

  const normalizeCategory = (category?: string) => {
    const value = (category || "").toLowerCase();
    if (value.includes("residential") || value.includes("housing")) return "Residential";
    if (value.includes("health")) return "Healthcare";
    if (value.includes("commercial") || value.includes("office") || value.includes("building")) return "Commercial";
    if (value.includes("industrial") || value.includes("factory") || value.includes("mill")) return "Industrial";
    return category || "Commercial";
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-white py-10">
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <motion.div
          className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500">
                Hamdan Construction
              </div>
              <motion.h1
                className="mt-2 text-3xl font-bold text-gray-900"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                {projectHeading}
              </motion.h1>
              <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
                A portfolio of civil, residential and infrastructure work
                delivered across Oman.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {filterCategories.map((label) => (
                <button
                  key={label}
                  type="button"
                  className="text-sm px-4 py-1.5 rounded-full border border-yellow-400 bg-yellow-400 text-gray-950 shadow-sm"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {projectsList.map((project: any, idx: number) => {
            const category = normalizeCategory(project.category);
            const Icon = iconMap[project.iconName] || iconMap[category === "Healthcare" ? "Hospital" : category === "Residential" ? "Home" : category === "Industrial" ? "Factory" : "Building2"] || Building2;
            return (
              <motion.div
                key={`${project.title || "project"}-${idx}`}
                variants={itemVariants}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" },
                }}
                className="group relative"
              >
                <div
                  className="rounded-2xl border border-yellow-300 bg-[#fffaf0] p-8 shadow-lg shadow-gray-200/70 hover:shadow-xl transition-all duration-300 h-full min-h-[300px] flex flex-col"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-700 text-white shadow-xl shadow-orange-200 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                      <Icon className="w-7 h-6" />
                    </div>
                    <span className="text-sm px-4 py-2 rounded-full bg-white border border-gray-100 shadow-sm font-semibold text-gray-900">
                      {category}
                    </span>
                  </div>

                  <div className="mt-9 flex flex-col flex-1">
                    <h3 className="text-[20px] font-bold text-gray-950 leading-snug mb-5">
                      {project.title}
                    </h3>

                    <p className="text-gray-800 text-[14px] leading-relaxed mb-8 flex-1">
                      {project.desc}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-green-600">
                        <div className="w-2.5 h-2.5 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-sm font-bold">{project.status || "Completed"}</span>
                      </div>
                      <Calendar className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
};

export default ConstructProject;



